import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "../../ui/textarea";
import { useAuth } from "../../../context/AuthContext";
import { supabase } from "../../../lib/supabase";
import { staffDirectory, fmtDateTime } from "../../../lib/adminApi";

export function AdminNotes({ profileId }) {
  const { user, role } = useAuth();
  const [notes, setNotes] = useState([]);
  const [staff, setStaff] = useState({});
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase.from("admin_notes").select("*").eq("profile_id", profileId).order("created_at", { ascending: false });
    setNotes(data || []);
  }, [profileId]);
  useEffect(() => { load(); staffDirectory().then(setStaff); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    const { error } = await supabase.from("admin_notes").insert({ profile_id: profileId, note: text.trim(), created_by: user.id });
    setBusy(false);
    if (error) return toast.error(error.message);
    setText("");
    load();
  };
  const remove = async (id) => {
    const { error } = await supabase.from("admin_notes").delete().eq("id", id);
    if (error) toast.error(error.message); else load();
  };

  return (
    <section className="rmm-card p-5 sm:p-6" data-testid="admin-notes">
      <h3 className="font-display text-2xl font-semibold text-[#7A0016]">Internal notes</h3>
      <p className="mb-4 text-xs text-[#8C6A6C]">Visible to staff only. Never shown to clients.</p>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Add a note..." data-testid="note-input" />
      <button type="button" onClick={add} disabled={busy || !text.trim()} data-testid="note-add-button" className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#7A0016] px-5 py-2 text-sm font-semibold text-[#F5E6BA] hover:bg-[#4A000E] disabled:opacity-50">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Add note
      </button>
      <ul className="mt-5 space-y-3">
        {notes.length === 0 && <li className="text-sm text-[#8C6A6C]">No notes yet.</li>}
        {notes.map((n) => (
          <li key={n.id} data-testid={`note-${n.id}`} className="rounded-lg border border-[#D4AF37]/30 bg-[#FAFAF7] p-3">
            <p className="whitespace-pre-wrap text-sm text-[#1A1515]">{n.note}</p>
            <div className="mt-2 flex items-center justify-between text-xs text-[#8C6A6C]">
              <span>{staff[n.created_by] || "Staff"} · {fmtDateTime(n.created_at)}</span>
              {(n.created_by === user.id || role === "admin") && (
                <button type="button" onClick={() => remove(n.id)} data-testid={`note-delete-${n.id}`} className="text-[#A31828] hover:text-[#7A0016]" aria-label="Delete note"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
